import { t } from '@/utils/i18n'
import { useRef, useState, useMemo } from 'react'
import { useSetState, useUpdateEffect  } from 'ahooks'
import useUrlState from "@ahooksjs/use-url-state"
import { NavBar, ErrorBlock, Dropdown, List, InfiniteScroll, PullToRefresh } from 'antd-mobile'
import dayjs from 'dayjs'
import KpSearch from '@/components/KpSearch'
import KpRangeDate from '@/components/KpRangeDate'
import { useNavigate, useLocation } from 'react-router-dom'
import type { Order } from '@/types/interfaces'
import useCorpInfoStore from '@/stores/corp-info'
import { utils, image, apis, constant, routesMap } from '@/utils'
import TopTabbar from '../components/TopTabbar'
import OrderItem from '../components/OrderItem'
// import OrderSummary from '../components/OrderSummary'
import styles from './index.module.less'
import { Times } from '@/components/KpRangeDate'

const { orderApi } = apis
const { to, getLatestYear } = utils
const { getAbsoluteUrlByStrings } = image
const {
  order: { QueryType },
  product: { IMAGE_DEFAULT },
} = constant

function OrderList() {
  const { corpInfo } = useCorpInfoStore()
  const { accountId, corpId } = corpInfo
  const location = useLocation()
  const orderStatus = location?.state?.orderStatus || QueryType.ALL
  const dateRef = useRef<any>(null)
  const [urlData, setUrlData] = 
    useUrlState<{ 
      queryType: string, searchText: string,
      startTime: number, endTime: number,
    }>({
      queryType: orderStatus,
      searchText: '',
      startTime: getLatestYear().startDate.getTime(),
      endTime: getLatestYear().endDate.getTime(),
    })
  const times = useMemo(() => {
    return {
      startTime: Number(urlData.startTime),
      endTime: Number(urlData.endTime),
    }
  }, [urlData])
  const navigate = useNavigate()
  const [pageInfo, setPageInfo] = useSetState({
    pageNum: 0,
    pageSize: 20,
  })
  const [orderList, setOrderList] = useState<Order[]>([])
  const {
    hasNextPage,
    isDataEmpty,
    runAsync: onGetOrderList,
  } = orderApi.useQueryOrderList({})

  useUpdateEffect(() => {
    getOrderList()
  }, [urlData])

  // 加载下一页
  async function loadNextPage() {
    if (!hasNextPage) return
    const pageNum = pageInfo.pageNum + 1
    setPageInfo({ pageNum })
    return getOrderList(pageNum)
  }

  async function handleRefresh() {
    const pageNum = 1
    setPageInfo({ pageNum })
    return getOrderList(pageNum)
  }

  // 获取过滤数据
  function getFilterData() {
    const params = {
      accountId,
      storeId: -1,
      corpId,
      pageModel: 1, //  分页模式 1pageNum分页 无时间分页
      ...pageInfo,
      ...urlData,
    }
    return params
  }

  // 获取订单列表
  async function getOrderList(_pageNum?: number) {
    const pageNum = _pageNum || pageInfo.pageNum
    const filterData = getFilterData()
    filterData.pageNum = pageNum
    const [err, resData] = await to(onGetOrderList(filterData))
    if (err) {
      return
    }

    // 处理数据
    resData.forEach((order: Order) => {
      handleOrder(order)
    })

    if (pageNum === 1) {
      setOrderList(resData)
    } else {
      setOrderList(orderList.concat(resData))
    }
  }

  // 处理单据数据
  function handleOrder(order: Order) {
    const {
      productImage,
      orderExpressEntities,
      touristHiddenPrice,
      completeStatusTime,
    } = order

    const productImgs = productImage
      ? getAbsoluteUrlByStrings(productImage, 140)
      : [IMAGE_DEFAULT]
    const expressObj =
      orderExpressEntities && orderExpressEntities.length > 0
        ? orderExpressEntities[0]
        : null
    const completeStatusTimeLabel = dayjs(Number(completeStatusTime)).format(
      'YYYY-MM-DD HH:mm'
    )
    const isTouristHiddenPrice = Boolean(touristHiddenPrice)

    Object.assign(order, {
      productImgs,
      expressObj,
      completeStatusTimeLabel,
      isTouristHiddenPrice,
    })
  }

  // 搜索变化
  function onSearchChange(value: string) {
    setUrlData({ searchText: value })
    setPageInfo({ pageNum: 1 })
  }

  // 确定日期
  function onDateConfirm(data: Times) {
    setUrlData(data)
    dateRef.current.close()
    setPageInfo({ pageNum: 1 })
  }

  // 订单状态变化
  function onStatusChange(value: string) {
    setUrlData({ queryType: value })
    setPageInfo({ pageNum: 1 })
  }

  return (
    <div className={styles['order-list-wrap']}>
      <NavBar
        children={t('我的订单')}
        className={styles['nav-bar']}
        onBack={() => navigate(routesMap.MyCenter)}
      />

      <div className={styles['operate-section']}>
        <div className={styles['operate-section-top']}>
          <KpSearch
            className={styles['search-bar']}
            value={urlData.searchText}
            placeholder={t('搜索')}
            onChange={onSearchChange}
          />

          <Dropdown
            ref={dateRef}
            closeOnClickAway
            className={styles['dropdown-box']}
          >
            <Dropdown.Item title={t('日期')} key="kpRangeDate">
              <KpRangeDate
                value={times}
                onCancel={() => dateRef.current.close()}
                onConfirm={onDateConfirm}
              ></KpRangeDate>
            </Dropdown.Item>
          </Dropdown>
        </div>

        <TopTabbar tagId={urlData.queryType} onChange={onStatusChange} />
      </div>

      <div className={styles['order-list']}>
        <PullToRefresh onRefresh={handleRefresh} >
          <List>
            {!!orderList.length &&
              orderList.map((item) => (
                <OrderItem key={item.orderId} order={item} />
              ))}
          </List>
          {isDataEmpty && (
            <ErrorBlock
              status="empty"
              fullPage
              title={t('找不到订单哦~')}
              description=""
            />
          )}
        </PullToRefresh>
        {!isDataEmpty && (
          <InfiniteScroll
            hasMore={hasNextPage}
            finished-text={t('没有更多了')}
            loadMore={loadNextPage}
          />
        )}
      </div>

      {/* {!!orderList.length && (
        <OrderSummary
          className={styles['footer']}
          sum={extend.sumReceivable}
          total={extend.sumQuantity}
        />
      )} */}
    </div>
  )
}

export default OrderList
